// src/components/dashboards/ParentDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import api from '../../services/api';
import StudentTransportViewTab from '../transport/StudentTransportViewTab';
import Calendar from '../shared/Calendar';

// ─── API helpers ─────────────────────────────────────────────────────────────
const parentAPI = {
  getDashboardStats: ()         => api.get('/auth/parent/dashboard/stats/').then(r => r.data),
  getLinkedStudents: ()         => api.get('/auth/parent/students/').then(r => r.data),
  getStudentDetail:  (id)       => api.get(`/auth/parent/students/${id}/`).then(r => r.data),
  getAttendance:     (id, year) => api.get(`/auth/parent/students/${id}/attendance/`, { params: { year } }).then(r => r.data),
  getClasses:        (id)       => api.get(`/auth/parent/students/${id}/classes/`).then(r => r.data),
  getFees:           (id)       => api.get(`/auth/parent/students/${id}/fees/`).then(r => r.data),
  getLeaves:         (id)       => api.get(`/auth/parent/students/${id}/leaves/`).then(r => r.data),
  getAnnouncements:  (id)       => api.get(`/auth/parent/students/${id}/announcements/`).then(r => r.data),
};

// ─── Responsive global styles injected once ──────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  :root {
    --navy:        #0f1f45;
    --blue:        #2563eb;
    --blue-lt:     #dbeafe;
    --blue-mid:    #eff6ff;
    --green:       #059669;
    --green-lt:    #d1fae5;
    --amber:       #d97706;
    --amber-lt:    #fef3c7;
    --red:         #dc2626;
    --red-lt:      #fee2e2;
    --purple:      #7c3aed;
    --purple-lt:   #ede9fe;
    --slate:       #64748b;
    --muted:       #94a3b8;
    --border:      #e2e8f0;
    --bg:          #f1f5f9;
    --card:        #ffffff;
    --text:        #0f172a;
    --font:        'Plus Jakarta Sans', 'Segoe UI', sans-serif;
    --radius-sm:   8px;
    --radius-md:   12px;
    --radius-lg:   16px;
    --shadow-sm:   0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
    --shadow-md:   0 4px 12px rgba(0,0,0,.08);
  }

  .pd-root * { box-sizing: border-box; }
  .pd-root { font-family: var(--font); color: var(--text); }

  /* ── KPI grid ── */
  .pd-kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  @media (max-width: 1024px) { .pd-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px)  { .pd-kpi-grid { grid-template-columns: 1fr 1fr; gap: 8px; } }

  /* ── KPI card ── */
  .pd-kpi {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: var(--shadow-sm);
    min-width: 0;
    overflow: hidden;
  }
  .pd-kpi-icon {
    width: 44px; height: 44px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }
  .pd-kpi-val  { font-size: 20px; font-weight: 800; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pd-kpi-lbl  { font-size: 11px; font-weight: 700; margin-top: 3px; }
  .pd-kpi-sub  { font-size: 10px; color: var(--muted); margin-top: 2px; }

  @media (max-width: 480px) {
    .pd-kpi        { padding: 12px 10px; gap: 8px; }
    .pd-kpi-icon   { width: 36px; height: 36px; font-size: 16px; }
    .pd-kpi-val    { font-size: 16px; }
    .pd-kpi-lbl    { font-size: 10px; }
  }

  /* ── Student selector ── */
  .pd-student-tabs {
    display: flex; gap: 8px; flex-wrap: wrap;
  }
  .pd-student-tab {
    padding: 8px 16px;
    border-radius: 10px;
    border: 2px solid var(--border);
    background: var(--card);
    color: var(--slate);
    font-weight: 600; font-size: 13px;
    cursor: pointer; font-family: var(--font);
    transition: all .15s;
    white-space: nowrap;
  }
  .pd-student-tab.active {
    border-color: var(--blue);
    background: var(--blue-lt);
    color: var(--blue);
  }

  /* ── Hero card ── */
  .pd-hero {
    background: linear-gradient(135deg, var(--navy) 0%, #1e40af 60%, var(--blue) 100%);
    border-radius: var(--radius-lg);
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    overflow: hidden;
  }
  .pd-hero-avatar {
    width: 56px; height: 56px;
    border-radius: 14px;
    background: rgba(255,255,255,.15);
    display: flex; align-items: center; justify-content: center;
    font-size: 26px;
    border: 2px solid rgba(255,255,255,.3);
    flex-shrink: 0;
  }
  .pd-hero-info { flex: 1; min-width: 0; }
  .pd-hero-name { font-size: 20px; font-weight: 800; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pd-hero-sub  { font-size: 12px; color: rgba(255,255,255,.72); margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pd-hero-badge {
    padding: 5px 14px;
    border-radius: 20px;
    background: rgba(255,255,255,.15);
    border: 1px solid rgba(255,255,255,.3);
    color: #fff; font-size: 12px; font-weight: 700;
    white-space: nowrap; flex-shrink: 0;
  }
  @media (max-width: 480px) {
    .pd-hero       { padding: 16px; gap: 12px; }
    .pd-hero-name  { font-size: 16px; }
    .pd-hero-sub   { font-size: 11px; }
  }

  /* ── Tabs ── */
  .pd-tabs {
    display: flex;
    gap: 4px;
    background: #e8edf5;
    padding: 4px;
    border-radius: 12px;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .pd-tabs::-webkit-scrollbar { display: none; }
  .pd-tab-btn {
    padding: 9px 14px;
    border-radius: 9px;
    border: none;
    background: transparent;
    color: var(--slate);
    font-weight: 500; font-size: 13px;
    cursor: pointer; font-family: var(--font);
    transition: all .15s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .pd-tab-btn.active {
    background: var(--card);
    color: var(--navy);
    font-weight: 700;
    box-shadow: 0 1px 4px rgba(0,0,0,.10);
  }

  /* ── Cards & sections ── */
  .pd-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
  }
  .pd-card-header {
    padding: 14px 18px;
    border-bottom: 1px solid #f1f5f9;
    font-weight: 700; font-size: 14px; color: var(--text);
  }

  /* ── Profile grid ── */
  .pd-profile-grid {
    padding: 20px 24px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 16px;
  }
  @media (max-width: 600px) {
    .pd-profile-grid { grid-template-columns: repeat(2, 1fr); padding: 16px; gap: 12px; }
  }

  /* ── Fee mini grid ── */
  .pd-fee-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  @media (max-width: 900px)  { .pd-fee-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 400px)  { .pd-fee-grid { grid-template-columns: 1fr 1fr; gap: 8px; } }

  /* ── Stats row (attendance / classes) ── */
  .pd-stat-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  @media (max-width: 768px) { .pd-stat-row { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 400px) { .pd-stat-row { grid-template-columns: 1fr 1fr; gap: 8px; } }

  /* ── Table wrapper ── */
  .pd-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .pd-table {
    width: 100%; border-collapse: collapse; min-width: 480px;
  }
  .pd-table th {
    padding: 9px 14px; text-align: left;
    font-size: 10px; font-weight: 700; color: var(--muted);
    text-transform: uppercase; letter-spacing: .05em;
    border-bottom: 1px solid var(--border);
    background: #f8fafc;
    white-space: nowrap;
  }
  .pd-table td {
    padding: 10px 14px;
    border-bottom: 1px solid #f8fafc;
    font-size: 13px;
  }
  .pd-table tr:last-child td { border-bottom: none; }
  .pd-table tr:nth-child(even) td { background: #fafafa; }

  /* ── Announcement cards ── */
  .pd-announce { display: flex; flex-direction: column; gap: 10px; }
  .pd-announce-card {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius-md); padding: 14px 18px;
  }

  /* ── Leave cards ── */
  .pd-leave-card {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius-md); padding: 16px 20px;
    display: flex; flex-direction: column; gap: 10px;
  }
  .pd-leave-head {
    display: flex; justify-content: space-between;
    align-items: flex-start; gap: 12px; flex-wrap: wrap;
  }

  /* ── Class cards ── */
  .pd-class-card {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius-md); padding: 16px 20px;
  }
  .pd-class-meta {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px; margin-top: 12px;
  }

  /* ── Progress bar ── */
  .pd-prog-track {
    height: 12px; background: #f1f5f9; border-radius: 6px; overflow: hidden;
  }
  .pd-prog-fill {
    height: 100%; border-radius: 6px; transition: width .6s ease;
  }

  /* ── Alert boxes ── */
  .pd-alert-warn {
    padding: 11px 15px; border-radius: var(--radius-sm);
    background: var(--red-lt); border: 1px solid #fecaca;
    color: var(--red); font-size: 13px; font-weight: 700;
  }
  .pd-alert-info {
    padding: 11px 15px; border-radius: var(--radius-sm);
    background: var(--blue-mid); border: 1px solid #bfdbfe;
    color: #1d4ed8; font-size: 12px;
  }

  /* ── Filter pill row ── */
  .pd-filter-row {
    display: flex; gap: 6px; flex-wrap: wrap;
  }
  .pd-filter-pill {
    padding: 6px 14px; border-radius: 20px; border: none;
    font-size: 12px; font-weight: 600; cursor: pointer;
    font-family: var(--font); transition: all .15s;
  }

  /* ── Section header ── */
  .pd-sec-hdr { margin-bottom: 12px; }
  .pd-sec-hdr h3 { margin: 0; font-size: 15px; font-weight: 800; color: var(--text); }
  .pd-sec-hdr p  { margin: 3px 0 0; font-size: 12px; color: var(--slate); }

  /* ── Spinner ── */
  @keyframes pd-spin { to { transform: rotate(360deg); } }
  .pd-spinner {
    width: 20px; height: 20px;
    border: 2px solid var(--blue-lt);
    border-top-color: var(--blue);
    border-radius: 50%;
    animation: pd-spin .7s linear infinite;
  }

  /* ── Badge ── */
  .pd-badge {
    display: inline-block;
    padding: 2px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700;
    white-space: nowrap;
  }

  /* ── Empty state ── */
  .pd-empty {
    text-align: center; padding: 36px 20px;
    color: var(--muted); background: #f8fafc;
    border-radius: var(--radius-md);
    border: 2px dashed var(--border); font-size: 13px;
  }

  /* ── Year selector ── */
  .pd-year-select {
    padding: 7px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 13px; background: var(--card);
    font-family: var(--font); cursor: pointer;
    color: var(--text);
  }
`;

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
const fmtDate = iso =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtINR = v =>
  `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
const pctColor = p => p >= 75 ? 'var(--green)'  : p >= 50 ? 'var(--amber)'  : 'var(--red)';
const pctBg    = p => p >= 75 ? 'var(--green-lt)': p >= 50 ? 'var(--amber-lt)': 'var(--red-lt)';

const STATUS_COL = {
  PRESENT: { c:'var(--green)',  bg:'var(--green-lt)' },
  LATE:    { c:'var(--amber)',  bg:'var(--amber-lt)' },
  ABSENT:  { c:'var(--red)',    bg:'var(--red-lt)'   },
  EXCUSED: { c:'var(--purple)', bg:'var(--purple-lt)'},
};
const LEAVE_COL = {
  PENDING:  { c:'var(--amber)', bg:'var(--amber-lt)' },
  APPROVED: { c:'var(--green)', bg:'var(--green-lt)' },
  REJECTED: { c:'var(--red)',   bg:'var(--red-lt)'   },
};
const CLASS_COL = {
  PENDING:   { c:'#92400e',      bg:'var(--amber-lt)' },
  ONGOING:   { c:'var(--blue)',  bg:'var(--blue-lt)'  },
  COMPLETED: { c:'var(--green)', bg:'var(--green-lt)' },
  CANCELLED: { c:'var(--slate)', bg:'#f1f5f9'          },
};

// ─── Shared micro-components ──────────────────────────────────────────────────
function Badge({ label, color, bg }) {
  return <span className="pd-badge" style={{ color, background: bg }}>{label}</span>;
}

function KpiCard({ icon, title, value, sub, color, bg }) {
  return (
    <div className="pd-kpi">
      <div className="pd-kpi-icon" style={{ background: bg }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div className="pd-kpi-val" style={{ color: color || 'var(--text)' }}>{value}</div>
        <div className="pd-kpi-lbl" style={{ color }}>{title}</div>
        {sub && <div className="pd-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="pd-sec-hdr">
      <h3>{title}</h3>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function EmptyState({ icon = '📭', text = 'No data available' }) {
  return (
    <div className="pd-empty">
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      {text}
    </div>
  );
}

function LoadSpinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding: 48, gap: 10, color:'var(--slate)' }}>
      <div className="pd-spinner" />
      Loading…
    </div>
  );
}

function MiniStatBox({ label, value, color, bg }) {
  return (
    <div style={{ background: bg, borderRadius: 'var(--radius-md)', padding: '12px 16px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function ProgressBar({ pct, color }) {
  return (
    <div className="pd-prog-track">
      <div className="pd-prog-fill" style={{ width:`${Math.min(pct,100)}%`, background: color }} />
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ student, announcements }) {
  if (!student) return <LoadSpinner />;
  const profile = student.profile || {};
  const fr      = student.financial_record || {};

  const fields = [
    ['Student ID',  profile.student_id || '—'],
    ['Course',      profile.course_display || profile.course || '—'],
    ['Department',  profile.student_department_name || '—'],
    ['Semester',    profile.current_semester || '—'],
    ['Enroll Year', profile.enrollment_year || '—'],
    ['Blood Group', profile.blood_group || '—'],
    ['Gender',      profile.gender === 'M' ? 'Male' : profile.gender === 'F' ? 'Female' : profile.gender || '—'],
    ['Phone',       student.phone_number || '—'],
    ['Email',       student.email || '—'],
  ];

  const paid    = parseFloat(fr.tuition_paid || 0);
  const total   = parseFloat(fr.tuition_fee || 0);
  const pct     = total > 0 ? Math.round(paid / total * 100) : 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap: 20 }}>
      {/* Profile card */}
      <div className="pd-card">
        <div style={{ background:'linear-gradient(135deg,var(--navy),var(--blue))', padding:'22px 24px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div style={{ width:60,height:60,borderRadius:14,background:'rgba(255,255,255,.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,border:'2px solid rgba(255,255,255,.3)',flexShrink:0 }}>🎓</div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:20,fontWeight:800,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
              {student.full_name || `${student.first_name} ${student.last_name}`.trim()}
            </div>
            <div style={{ fontSize:12,color:'rgba(255,255,255,.7)',marginTop:4 }}>{profile.course_display || profile.course}</div>
          </div>
        </div>
        <div className="pd-profile-grid">
          {fields.map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3 }}>{l}</div>
              <div style={{ fontSize:13,fontWeight:600,color:'var(--text)',wordBreak:'break-word' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Fee snapshot */}
      {fr && (
        <div>
          <SectionHeader title="Fee Summary" sub="Current fee status for this student" />
          <div className="pd-fee-grid" style={{ marginBottom: 12 }}>
            <KpiCard icon="💰" title="Total Fee"   value={fmtINR(fr.tuition_fee)}     color="var(--purple)" bg="var(--purple-lt)" />
            <KpiCard icon="✅" title="Paid"         value={fmtINR(fr.tuition_paid)}    color="var(--green)"  bg="var(--green-lt)" />
            <KpiCard icon="⏳" title="Pending"      value={fmtINR(fr.tuition_pending)} color="var(--amber)"  bg="var(--amber-lt)" />
            <KpiCard icon="📅" title="Due Date"    value={fmtDate(fr.payment_deadline)} color={fr.is_overdue?'var(--red)':'var(--slate)'} bg={fr.is_overdue?'var(--red-lt)':'#f1f5f9'} />
          </div>
          <div className="pd-card" style={{ padding: 18 }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
              <span style={{ fontSize:13,fontWeight:700 }}>Payment Progress</span>
              <span style={{ fontSize:18,fontWeight:800,color:'var(--green)' }}>{pct}%</span>
            </div>
            <ProgressBar pct={pct} color="linear-gradient(90deg,var(--green),#34d399)" />
            <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--muted)',marginTop:6 }}>
              <span>Paid: {fmtINR(paid)}</span>
              <span>Remaining: {fmtINR(parseFloat(fr.tuition_pending||0))}</span>
            </div>
          </div>
          {fr.is_overdue && <div className="pd-alert-warn" style={{ marginTop:10 }}>⚠️ Payment is overdue. Please contact the finance office.</div>}
        </div>
      )}

      {/* Announcements */}
      {announcements && announcements.length > 0 && (
        <div>
          <SectionHeader title="Latest Announcements" sub="From assigned professor" />
          <div className="pd-announce">
            {announcements.slice(0, 3).map(a => (
              <div key={a.id} className="pd-announce-card">
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10,marginBottom:6,flexWrap:'wrap' }}>
                  <span style={{ fontSize:14,fontWeight:700 }}>{a.title}</span>
                  <Badge label={a.priority}
                    color={a.priority==='URGENT'?'var(--red)':a.priority==='HIGH'?'var(--amber)':'var(--blue)'}
                    bg={a.priority==='URGENT'?'var(--red-lt)':a.priority==='HIGH'?'var(--amber-lt)':'var(--blue-lt)'} />
                </div>
                <p style={{ margin:0,fontSize:13,color:'var(--slate)',lineHeight:1.6 }}>{a.content}</p>
                <div style={{ marginTop:8,fontSize:11,color:'var(--muted)' }}>By {a.faculty_name} · {fmtDate(a.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Attendance ──────────────────────────────────────────────────────────
function AttendanceTab({ studentId }) {
  const [data, setData]       = useState(null);
  const [year, setYear]       = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    parentAPI.getAttendance(studentId, year)
      .then(d => setData(d)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId, year]);

  if (loading) return <LoadSpinner />;
  const stats   = data?.stats || {};
  const records = data?.records || [];
  const pct     = stats.attendance_pct ?? 0;

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:20 }}>
      {/* Year selector */}
      <div style={{ display:'flex',gap:10,alignItems:'center' }}>
        <label style={{ fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'.05em' }}>Year</label>
        <select className="pd-year-select" value={year} onChange={e => setYear(Number(e.target.value))}>
          {[new Date().getFullYear(), new Date().getFullYear()-1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* KPIs */}
      <div className="pd-stat-row">
        <MiniStatBox label="Total"   value={stats.total??0}   color="#334155"        bg="#f1f5f9" />
        <MiniStatBox label="Present" value={stats.present??0} color="var(--green)"   bg="var(--green-lt)" />
        <MiniStatBox label="Absent"  value={stats.absent??0}  color="var(--red)"     bg="var(--red-lt)" />
        <MiniStatBox label="Excused" value={stats.excused??0} color="var(--purple)"  bg="var(--purple-lt)" />
      </div>

      {/* Attendance meter */}
      <div className="pd-card" style={{ padding:'18px 20px' }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <span style={{ fontSize:14,fontWeight:700 }}>Overall Attendance Rate</span>
          <span style={{ fontSize:24,fontWeight:800,color:pctColor(pct) }}>{pct}%</span>
        </div>
        <ProgressBar pct={pct} color={pctColor(pct)} />
        <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--muted)',marginTop:8 }}>
          <span style={{ color:pctColor(pct),fontWeight:600 }}>
            {pct >= 75 ? '✓ Above 75% threshold' : '⚠ Below 75% minimum'}
          </span>
          <span>75% required</span>
        </div>
      </div>

      {/* Records table */}
      <div className="pd-card">
        <div className="pd-card-header">Attendance Records ({records.length})</div>
        {records.length === 0
          ? <div style={{ padding:20 }}><EmptyState icon="📋" text={`No records for ${year}`} /></div>
          : (
            <div className="pd-table-wrap">
              <table className="pd-table">
                <thead>
                  <tr>
                    {['Class / Subject','Date','Status','Confirmed'].map(h => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => {
                    const ac = STATUS_COL[r.status] || { c:'var(--slate)', bg:'#f1f5f9' };
                    return (
                      <tr key={r.id}>
                        <td style={{ fontWeight:600,color:'var(--text)' }}>{r.class_title||'—'}</td>
                        <td style={{ color:'var(--slate)',fontFamily:'monospace' }}>{r.attendance_date||'—'}</td>
                        <td><Badge label={r.status_display||r.status} color={ac.c} bg={ac.bg} /></td>
                        <td>
                          {r.is_confirmed
                            ? <span style={{ color:'var(--green)',fontWeight:700,fontSize:12 }}>✓ Yes</span>
                            : <span style={{ color:'var(--amber)',fontSize:12 }}>⏱ Pending</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Tab: Classes ─────────────────────────────────────────────────────────────
function ClassesTab({ studentId }) {
  const [classes,  setClasses]  = useState([]);
  const [filter,   setFilter]   = useState('all');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    parentAPI.getClasses(studentId)
      .then(d => setClasses(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => setClasses([]))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <LoadSpinner />;

  const counts = { PENDING:0, ONGOING:0, COMPLETED:0, CANCELLED:0 };
  classes.forEach(c => { if (counts[c.status] !== undefined) counts[c.status]++; });
  const displayed = filter === 'all' ? classes : classes.filter(c => c.status === filter);

  const filters = [
    { key:'all',       label:'All',       color:'#334155', bg:'#f1f5f9' },
    { key:'PENDING',   label:'Pending',   color:'#92400e', bg:'var(--amber-lt)' },
    { key:'ONGOING',   label:'Ongoing',   color:'var(--blue)', bg:'var(--blue-lt)' },
    { key:'COMPLETED', label:'Completed', color:'var(--green)', bg:'var(--green-lt)' },
  ];

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
      {/* Stats */}
      <div className="pd-stat-row">
        <MiniStatBox label="Total"     value={classes.length}    color="#334155"        bg="#f1f5f9" />
        <MiniStatBox label="Pending"   value={counts.PENDING}    color="#92400e"        bg="var(--amber-lt)" />
        <MiniStatBox label="Ongoing"   value={counts.ONGOING}    color="var(--blue)"    bg="var(--blue-lt)" />
        <MiniStatBox label="Completed" value={counts.COMPLETED}  color="var(--green)"   bg="var(--green-lt)" />
      </div>

      {/* Filter pills */}
      <div className="pd-filter-row">
        {filters.map(f => (
          <button key={f.key} className="pd-filter-pill"
            onClick={() => setFilter(f.key)}
            style={{
              background: filter === f.key ? f.bg : '#f1f5f9',
              color: filter === f.key ? f.color : 'var(--slate)',
              border: filter === f.key ? `2px solid ${f.color}20` : '2px solid transparent',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Class cards */}
      {displayed.length === 0
        ? <EmptyState icon="📚" text="No classes found" />
        : displayed.map(cs => {
          const sc = CLASS_COL[cs.status] || CLASS_COL.PENDING;
          return (
            <div key={cs.id} className="pd-class-card">
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,flexWrap:'wrap' }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:800,fontSize:15,marginBottom:3,overflow:'hidden',textOverflow:'ellipsis' }}>{cs.title}</div>
                  <div style={{ fontSize:12,color:'var(--slate)' }}>{cs.department_name} · {cs.year_label}</div>
                  {cs.time_slot_name && (
                    <div style={{ fontSize:12,color:'var(--blue)',marginTop:2 }}>
                      ⏰ {cs.time_slot_name} · {cs.time_slot_start?.slice(0,5)}–{cs.time_slot_end?.slice(0,5)}
                    </div>
                  )}
                </div>
                <Badge label={cs.status} color={sc.c} bg={sc.bg} />
              </div>
              <div className="pd-class-meta">
                {[['📅 Start',fmtDate(cs.start_date)],['🏁 End',fmtDate(cs.end_date)],['👤 Faculty',cs.assigned_to_name||'Not assigned']].map(([l,v])=>(
                  <div key={l} style={{ background:'#f8fafc',borderRadius:8,padding:'8px 12px' }}>
                    <div style={{ fontSize:10,color:'var(--muted)',fontWeight:600,marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:13,fontWeight:600 }}>{v}</div>
                  </div>
                ))}
              </div>
              {cs.status_note && (
                <div className="pd-alert-info" style={{ marginTop:10 }}>💬 {cs.status_note}</div>
              )}
            </div>
          );
        })}
    </div>
  );
}

// ─── Tab: Fees ────────────────────────────────────────────────────────────────
function FeesTab({ studentId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    parentAPI.getFees(studentId)
      .then(d => setData(d)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <LoadSpinner />;
  const fr  = data?.financial_record    || {};
  const txn = data?.recent_transactions || [];

  const paid    = parseFloat(fr.tuition_paid    || 0);
  const total   = parseFloat(fr.tuition_fee     || 0);
  const pending = parseFloat(fr.tuition_pending || 0);
  const pct     = total > 0 ? Math.round(paid / total * 100) : 0;

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:20 }}>
      <div className="pd-fee-grid">
        <KpiCard icon="📋" title="Total Fee"  value={fmtINR(fr.tuition_fee)}     color="var(--purple)" bg="var(--purple-lt)" />
        <KpiCard icon="✅" title="Paid"        value={fmtINR(fr.tuition_paid)}    color="var(--green)"  bg="var(--green-lt)" />
        <KpiCard icon="⏳" title="Pending"     value={fmtINR(fr.tuition_pending)} color={fr.is_overdue?'var(--red)':'var(--amber)'} bg={fr.is_overdue?'var(--red-lt)':'var(--amber-lt)'} />
        <KpiCard icon="📅" title="Due Date"   value={fmtDate(fr.payment_deadline)} color={fr.is_overdue?'var(--red)':'var(--slate)'} bg={fr.is_overdue?'var(--red-lt)':'#f1f5f9'} />
      </div>

      <div className="pd-card" style={{ padding:'18px 20px' }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
          <span style={{ fontSize:14,fontWeight:700 }}>Payment Progress</span>
          <span style={{ fontSize:22,fontWeight:800,color:'var(--green)' }}>{pct}%</span>
        </div>
        <ProgressBar pct={pct} color="linear-gradient(90deg,var(--green),#34d399)" />
        <div style={{ display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--muted)',marginTop:6,flexWrap:'wrap',gap:4 }}>
          <span>Paid: {fmtINR(paid)}</span>
          <span>Remaining: {fmtINR(pending)}</span>
        </div>
        {fr.is_overdue && <div className="pd-alert-warn" style={{ marginTop:12 }}>⚠️ Payment is overdue. Please contact the finance office immediately.</div>}
        {fr.last_payment_date && <div style={{ marginTop:8,fontSize:12,color:'var(--slate)' }}>Last payment: {fmtDate(fr.last_payment_date)}</div>}
      </div>

      <div>
        <SectionHeader title="Recent Transactions" sub={`Last ${txn.length} payment(s)`} />
        {txn.length === 0
          ? <EmptyState icon="💳" text="No transactions found" />
          : (
            <div className="pd-card">
              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>{['Date','Type','Amount','Method','Status'].map(h=><th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {txn.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontFamily:'monospace',color:'var(--slate)',fontSize:12 }}>{fmtDate(t.created_at)}</td>
                        <td style={{ fontWeight:600 }}>{t.transaction_type_display || t.transaction_type}</td>
                        <td style={{ fontWeight:700,color:'var(--green)' }}>{fmtINR(t.amount)}</td>
                        <td style={{ color:'var(--slate)' }}>{t.payment_method || '—'}</td>
                        <td><Badge label={t.status||'—'}
                          color={t.status==='COMPLETED'?'var(--green)':'var(--amber)'}
                          bg={t.status==='COMPLETED'?'var(--green-lt)':'var(--amber-lt)'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Tab: Leaves ──────────────────────────────────────────────────────────────
function LeavesTab({ studentId }) {
  const [leaves,  setLeaves]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    parentAPI.getLeaves(studentId)
      .then(d => setLeaves(Array.isArray(d) ? d : (d.results || [])))
      .catch(() => setLeaves([]))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <LoadSpinner />;

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
      <SectionHeader title="Leave Applications" sub="All leave applications submitted by this student" />
      {leaves.length === 0
        ? <EmptyState icon="🗓️" text="No leave applications found" />
        : leaves.map(lv => {
          const sc = LEAVE_COL[lv.status] || { c:'var(--slate)', bg:'#f1f5f9' };
          return (
            <div key={lv.id} className="pd-leave-card">
              <div className="pd-leave-head">
                <div>
                  <div style={{ fontWeight:800,fontSize:14 }}>{lv.leave_type_display}</div>
                  <div style={{ fontSize:12,color:'var(--slate)',marginTop:3 }}>
                    {fmtDate(lv.from_date)} → {fmtDate(lv.to_date)} · {lv.duration_days} day{lv.duration_days!==1?'s':''}
                  </div>
                </div>
                <Badge label={lv.status} color={sc.c} bg={sc.bg} />
              </div>
              <p style={{ margin:0,fontSize:13,color:'var(--slate)',lineHeight:1.6 }}>{lv.reason}</p>
              {lv.rejection_reason && (
                <div className="pd-alert-warn">❌ Rejected: {lv.rejection_reason}</div>
              )}
              {lv.approved_by_name && lv.status === 'APPROVED' && (
                <div style={{ fontSize:12,color:'var(--green)',fontWeight:600 }}>✓ Approved by {lv.approved_by_name}</div>
              )}
              <div style={{ fontSize:11,color:'var(--muted)' }}>Applied {fmtDate(lv.created_at)}</div>
            </div>
          );
        })}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const TABS = [
  { key:'overview',   label:'📋 Overview'   },
  { key:'attendance', label:'📊 Attendance' },
  { key:'classes',    label:'📚 Classes'    },
  { key:'fees',       label:'💰 Fees'       },
  { key:'leaves',     label:'🗓️ Leaves'    },
  { key:'transport',  label:'🚌 Bus & Transport' },
  { key:'calendar',   label:'📅 Calendar' },
];

export default function ParentDashboard() {
  const { user } = useAuth();

  const [stats,          setStats]          = useState(null);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [selectedId,     setSelectedId]    = useState(null);
  const [studentDetail,  setStudentDetail] = useState(null);
  const [announcements,  setAnnouncements] = useState([]);
  const [activeTab,      setActiveTab]     = useState('overview');
  const [loading,        setLoading]       = useState(true);

  // Inject CSS once
  useEffect(() => {
    const id = 'pd-global-css';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = GLOBAL_CSS;
      document.head.appendChild(style);
    }
  }, []);

  // Bootstrap
  useEffect(() => {
    Promise.all([parentAPI.getDashboardStats(), parentAPI.getLinkedStudents()])
      .then(([s, ls]) => {
        setStats(s);
        const students = Array.isArray(ls) ? ls : [];
        setLinkedStudents(students);
        if (students.length > 0) setSelectedId(students[0].student);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Student detail
  useEffect(() => {
    if (!selectedId) return;
    setStudentDetail(null);
    setAnnouncements([]);
    Promise.all([parentAPI.getStudentDetail(selectedId), parentAPI.getAnnouncements(selectedId)])
      .then(([d, a]) => {
        setStudentDetail(d);
        setAnnouncements(Array.isArray(a) ? a : []);
      })
      .catch(console.error);
  }, [selectedId]);

  const selectedLink = linkedStudents.find(l => l.student === selectedId);

  if (loading) {
    return (
      <DashboardLayout title={`Welcome, ${user?.first_name}!`}>
        <div className="pd-root"><LoadSpinner /></div>
      </DashboardLayout>
    );
  }

  if (linkedStudents.length === 0) {
    return (
      <DashboardLayout title="Parent Portal">
        <div className="pd-root" style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:400 }}>
          <div style={{ textAlign:'center',maxWidth:380,padding:'0 20px' }}>
            <div style={{ fontSize:64,marginBottom:16 }}>👨‍👩‍👦</div>
            <h2 style={{ fontSize:22,fontWeight:800,color:'var(--text)',margin:'0 0 12px' }}>No students linked</h2>
            <p style={{ fontSize:14,color:'var(--slate)',lineHeight:1.7 }}>
              Your account is not linked to any student yet. Please contact the admin to link your child's account.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`Parent Portal — ${user?.first_name}`}>
      <div className="pd-root" style={{ display:'flex',flexDirection:'column',gap:16 }}>

        {/* Top KPIs */}
        {stats && (
          <div className="pd-kpi-grid">
            <KpiCard icon="🎓" title="Linked Students" value={stats.total_students}
              color="var(--navy)" bg="#e8eaf0" />
            <KpiCard icon="📊" title="Avg Attendance"  value={`${stats.attendance_pct ?? 0}%`}
              color={pctColor(stats.attendance_pct ?? 0)} bg={pctBg(stats.attendance_pct ?? 0)} />
            <KpiCard icon="💰" title="Pending Fees"    value={fmtINR(stats.total_pending_fees)}
              color="var(--amber)" bg="var(--amber-lt)" />
            <KpiCard icon="⏳" title="Pending Leaves"  value={stats.pending_leaves ?? 0}
              color="var(--slate)" bg="#f1f5f9" />
          </div>
        )}

        {/* Student selector — only shown when multiple */}
        {linkedStudents.length > 1 && (
          <div className="pd-student-tabs">
            {linkedStudents.map(l => (
              <button key={l.student} className={`pd-student-tab ${l.student === selectedId ? 'active' : ''}`}
                onClick={() => { setSelectedId(l.student); setActiveTab('overview'); }}>
                🎓 {l.student_name}
                <span style={{ fontSize:10,marginLeft:6,opacity:.7 }}>{l.relationship_display}</span>
              </button>
            ))}
          </div>
        )}

        {/* Hero banner */}
        {selectedLink && (
          <div className="pd-hero">
            <div className="pd-hero-avatar">🎓</div>
            <div className="pd-hero-info">
              <div className="pd-hero-name">{selectedLink.student_name}</div>
              <div className="pd-hero-sub">
                {selectedLink.student_course_display} · {selectedLink.student_dept || '—'} · {selectedLink.student_id_num || 'ID not assigned'}
              </div>
            </div>
            <div className="pd-hero-badge">{selectedLink.relationship_display}</div>
          </div>
        )}

        {/* Tab bar */}
        <div className="pd-tabs">
          {TABS.map(t => (
            <button key={t.key}
              className={`pd-tab-btn ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ minWidth:0 }}>
          {activeTab === 'overview'   && <OverviewTab student={studentDetail} announcements={announcements} />}
          {activeTab === 'attendance' && selectedId && <AttendanceTab studentId={selectedId} />}
          {activeTab === 'classes'    && selectedId && <ClassesTab   studentId={selectedId} />}
          {activeTab === 'fees'       && selectedId && <FeesTab      studentId={selectedId} />}
          {activeTab === 'leaves'     && selectedId && <LeavesTab    studentId={selectedId} />}
          {activeTab === 'transport'  && selectedId && <StudentTransportViewTab isParentView={true} studentId={selectedId} studentName={selectedLink?.student_name} />}
          {activeTab === 'calendar'   && <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}><Calendar /></div>}
        </div>
      </div>
    </DashboardLayout>
  );
}